import { Component, inject, signal, computed, effect, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FormTemplateService, FormFolderService } from '../../services';
import { FormTemplateListItem, FormFolder, FormTemplateListResponse, FormFolderListResponse } from '../../models';
import { ToastMessageService } from '@fitness-force/api';
import { TooltipModule } from 'primeng/tooltip';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { DataGridComponent, NoComponentComponent } from '@fitness-force/grid';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import {
  DateCellComponent,
  EntityStatusComponent,
  WithLinkComponent,
} from '@fitness-force/shared';
import { TENANTS_INFO } from '@fitness-force/state';
import { FolderManagementComponent } from '../folder-management/folder-management.component';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { DrawerModule } from 'primeng/drawer';
import { AuditTrailMiniComponent } from '@fitness-force/audit-trail';
import { FeatureFlagService } from '@fitness-force/shared-utils';

interface FolderFilterOption {
  name: string;
  value: number;
}

@Component({
  selector: 'lib-form-template-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    StyleClassModule,
    DialogModule,
    TooltipModule,
    TranslateModule,
    BreadcrumbComponent,
    DataGridComponent,
    FolderManagementComponent,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    DrawerModule,
    AuditTrailMiniComponent,
  ],
  templateUrl: './form-template-list.component.html',
  styleUrls: ['./form-template-list.component.scss'],
})
export class FormTemplateListComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly templateService = inject(FormTemplateService);
  private readonly folderService = inject(FormFolderService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  private readonly tenantInfo = toSignal(this.store.select(TENANTS_INFO));
  protected readonly tenantId = computed(
    () => this.tenantInfo()?.selected_tenant?.id?.toString() ?? '',
  );
  protected readonly tenantIdNumeric = computed(
    () => this.tenantInfo()?.selected_tenant?.id ?? 0,
  );

  protected readonly searchText = signal('');
  protected readonly selectedFolderId = signal<number>(0);
  protected selectedStatusFilter = '';

  protected readonly statusFilters = [
    { name: 'ADMIN.COMMON.ALL', value: '' },
    { name: 'ADMIN.COMMON.ACTIVE', value: 'active' },
    { name: 'ADMIN.COMMON.INACTIVE', value: 'inactive' },
  ];

  protected readonly folderFilterLabelKey = computed(() =>
    this.selectedFolderId() === 0 ? 'ADMIN.COMMON.ALL' : null,
  );

  protected readonly folderFilterDisplayName = computed(() => {
    const folderId = this.selectedFolderId();
    if (folderId === 0) {
      return '';
    }
    return (
      this.folderFilterOptions().find((option) => option.value === folderId)
        ?.name ?? ''
    );
  });

  protected readonly folderFilterOptions = computed<FolderFilterOption[]>(() => [
    { name: 'ADMIN.COMMON.ALL', value: 0 },
    ...this.folders().map((folder) => ({
      name: folder.name ?? '',
      value: folder.id ?? 0,
    })),
  ]);

  protected readonly showAuditTrailSidebar = signal(false);
  protected readonly showAuditLogsFeature = signal(false);

  protected readonly tableData = signal<FormTemplateListItem[]>([]);
  protected readonly folders = signal<FormFolder[]>([]);
  protected readonly columns = signal<any[]>([]);
  protected readonly loading = signal<boolean>(false);
  protected readonly emptyStateComponent = signal<any>(NoComponentComponent);
  protected readonly selectAllData = signal(false);
  protected readonly totalRecordLength = signal({
    totalPage: 1,
    totalItems: 0,
    currentPage: 0,
  });

  protected readonly tableData$ = new BehaviorSubject<FormTemplateListItem[]>([]);
  protected readonly columns$ = new BehaviorSubject<any[]>([]);
  protected readonly totalRecordLength$ = new BehaviorSubject({
    totalPage: 1,
    totalItems: 0,
    currentPage: 0,
  });
  protected readonly selectAllData$ = new BehaviorSubject(false);
  protected readonly emptyStateComponent$ = new BehaviorSubject<any>(NoComponentComponent);

  protected readonly showFolderDialog = signal(false);
  protected readonly showFolderListDialog = signal(false);
  protected readonly editingFolder = signal<FormFolder | null>(null);

  constructor() {
    this.featureFlagService.getFlag$('show_audit_trial').pipe(takeUntil(this.destroy$)).subscribe((isEnabled: boolean) => {
      this.showAuditLogsFeature.set(isEnabled);
    });

    effect(() => {
      const id = this.tenantId();
      if (id) {
        this.loadTemplates();
        this.loadFolders();
        this.setupColumns();
      }
    });

    effect(() => {
      this.tableData$.next(this.tableData());
    });

    effect(() => {
      this.columns$.next(this.columns());
    });

    effect(() => {
      this.totalRecordLength$.next(this.totalRecordLength());
    });

    effect(() => {
      this.selectAllData$.next(this.selectAllData());
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onSearch(value: string): void {
    this.searchText.set(value);
    this.loadTemplates();
  }

  protected onFolderFilterChange(folderId: number): void {
    this.selectedFolderId.set(folderId);
    this.loadTemplates();
  }

  protected onStatusFilterChange(value: string): void {
    this.selectedStatusFilter = value;
    this.loadTemplates();
  }

  protected showAuditTrail(): void {
    if (this.showAuditLogsFeature()) {
      this.showAuditTrailSidebar.set(true);
      this.cd.detectChanges();
    }
  }

  protected hideAuditTrail(): void {
    this.showAuditTrailSidebar.set(false);
    this.cd.detectChanges();
  }

  private loadTemplates(): void {
    const id = this.tenantId();
    if (!id) return;

    this.loading.set(true);
    const params: { search?: string; folder_id?: number; is_active?: boolean } = {};
    const search = this.searchText().trim();
    if (search) {
      params.search = search;
    }
    const folderId = this.selectedFolderId();
    if (folderId !== 0) {
      params.folder_id = folderId;
    }
    if (this.selectedStatusFilter === 'active') {
      params.is_active = true;
    } else if (this.selectedStatusFilter === 'inactive') {
      params.is_active = false;
    }

    this.templateService.listTemplates(id, params).subscribe({
      next: (response: FormTemplateListResponse) => {
        this.tableData.set(response.items);
        this.totalRecordLength.set({
          totalPage: 1,
          totalItems: response.items.length,
          currentPage: 0,
        });
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.toastService.showError(error);
        this.tableData.set([]);
        this.loading.set(false);
      },
    });
  }

  private loadFolders(): void {
    const id = this.tenantId();
    if (!id) return;

    this.folderService.listFolders(id).subscribe({
      next: (response: FormFolderListResponse) => {
        this.folders.set(response.items);
      },
      error: (error: unknown) => {
        this.toastService.showError(error);
      },
    });
  }

  private setupColumns(): void {
    this.translate
      .get([
        'SURVEY_FORMS.TITLE',
        'SURVEY_FORMS.FOLDER',
        'SURVEY_FORMS.VERSION',
        'SURVEY_FORMS.LAST_UPDATED_ON',
        'SURVEY_FORMS.STATUS',
      ])
      .subscribe((translations) => {
        this.columns.set([
          {
            title: translations['SURVEY_FORMS.TITLE'],
            classes: 'py-2 white-space-nowrap font-bold',
            sortable: true,
            filterable: true,
            dataProperty: 'title',
            type: 'component',
            data: {
              name: WithLinkComponent,
              fetch_key: 'title',
              additionData: true,
              aData: {
                keyName: 'title',
                uri: '/admin/form-templates/edit',
              },
            },
          },
          {
            title: translations['SURVEY_FORMS.FOLDER'],
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'folder_name',
            type: 'default',
          },
          {
            title: translations['SURVEY_FORMS.VERSION'],
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'version',
            type: 'default',
          },
          {
            title: translations['SURVEY_FORMS.LAST_UPDATED_ON'],
            classes: 'py-2 white-space-nowrap',
            sortable: true,
            filterable: true,
            dataProperty: 'updated_date',
            type: 'component',
            data: {
              name: DateCellComponent,
              additionData: true,
              aData: { fetch_key: 'updated_date' },
            },
          },
          {
            title: translations['SURVEY_FORMS.STATUS'],
            classes: 'py-2 white-space-nowrap',
            sortable: false,
            filterable: true,
            dataProperty: 'is_active',
            type: 'component',
            data: { name: EntityStatusComponent },
          },
          {
            title: '',
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'actions',
            type: 'component',
            data: {
              name: ActionsComponent,
              additionData: true,
              aData: ['edit', 'delete'],
            },
          },
        ]);
      });
  }

  protected onActionEvent(event: { action: string; columnData: FormTemplateListItem }): void {
    switch (event.action) {
      case 'edit':
        this.router.navigate([
          '/admin/form-templates/edit',
          String(event.columnData.id),
        ]);
        break;
      case 'status':
        this.toggleStatus(event.columnData);
        break;
      case 'delete':
        this.deleteTemplate(event.columnData);
        break;
    }
  }

  private toggleStatus(template: FormTemplateListItem): void {
    const id = this.tenantId();
    if (!id) return;

    const apiCall = template.is_active
      ? this.templateService.markTemplateAsInactive(id, String(template.id))
      : this.templateService.markTemplateAsActive(id, String(template.id));

    apiCall.subscribe({
      next: () => {
        const messageKey = template.is_active
          ? 'SURVEY_FORMS.TEMPLATE_DEACTIVATED'
          : 'SURVEY_FORMS.TEMPLATE_ACTIVATED';
        this.translate.get(messageKey).subscribe((message: string) => {
          this.toastService.showSuccess(message);
        });
        this.loadTemplates();
      },
      error: (error: unknown) => {
        this.toastService.showError(error);
      },
    });
  }

  private deleteTemplate(template: FormTemplateListItem): void {
    const id = this.tenantId();
    if (!id) return;

    this.templateService.deleteTemplate(id, String(template.id)).subscribe({
      next: () => {
        this.translate.get('SURVEY_FORMS.TEMPLATE_DELETED').subscribe((message: string) => {
          this.toastService.showSuccess(message);
        });
        this.loadTemplates();
      },
      error: (error: unknown) => {
        this.toastService.showError(error);
      },
    });
  }

  protected openFolderListDialog(): void {
    this.showFolderListDialog.set(true);
  }

  protected openCreateFolderFromManage(): void {
    this.editingFolder.set(null);
    this.showFolderDialog.set(true);
  }

  protected createFormFromManage(): void {
    this.showFolderListDialog.set(false);
    this.router.navigate(['/admin/form-templates/create']);
  }

  protected createFormInFolder(folderId?: number): void {
    this.showFolderListDialog.set(false);
    this.router.navigate(['/admin/form-templates/create'], {
      queryParams: folderId != null ? { folder_id: folderId } : {},
    });
  }

  protected onFolderSaved(): void {
    this.loadFolders();
    this.loadTemplates();
  }

  protected createNewTemplate(): void {
    this.router.navigate(['/admin/form-templates/create']);
  }
}
